/**
 * Diff puro de duas strings de `notes` (o texto de escopo que a Karla
 * escreve e o lead lê). Usado pra destacar em amarelo na proposta
 * pública o que FOI ADICIONADO depois da primeira visualização — o
 * cenário real do Alexandre 2026-04-24: lead abre proposta, envia
 * dúvida pedindo desconto, Karla ajusta o `notes` e adiciona uma
 * linha explicativa; quando o lead reabre, queremos que ele veja
 * claramente o que mudou (linha nova em fundo âmbar).
 *
 * Decisão de design — **só mostramos adicionados**, NÃO mostramos
 * removidos. Razão: o lead só precisa enxergar o que está ali agora
 * e o que é novo. Texto riscado de removido (`<s>`) polui a proposta
 * e confunde — a filosofia aqui é "proposta limpa + highlight do que
 * justifica o badge". Se a Karla REESCREVER um parágrafo, o diff
 * word-level detecta corretamente as palavras trocadas como adições
 * (com as palavras antigas descartadas silenciosamente).
 *
 * **Pure module**: sem React, sem side-effects. O componente wrapper
 * `NotesDiffView` consome o output e renderiza `<mark>`.
 */
import { diffWordsWithSpace } from 'diff';

/** Tipo discriminado pra cada pedaço do texto novo. Pedaços `added`
 * vêm com fundo amarelo na UI; `unchanged` renderiza normal. */
export interface NotesDiffPart {
  kind: 'added' | 'unchanged';
  value: string;
}

/** Compara `before` e `after` em granularidade de palavras+espaços.
 *
 * Retorna array de `NotesDiffPart` representando o texto `after`, com
 * cada palavra/segmento marcado como `added` (texto novo em relação
 * ao `before`) ou `unchanged` (apareceu em `before` também).
 *
 * Casos especiais:
 *  - `before` null/vazio → tudo em `after` é `added` (primeira versão
 *    após negociação não tinha snapshot; comportamento razoável).
 *  - `after` null/vazio → retorna array vazio.
 *  - textos idênticos → um único segmento `unchanged`.
 *  - parágrafos reordenados → o diff word-level vai detectar como
 *    remove+add; aceitamos — reordenação é mudança legítima pro lead.
 *
 * `diffWordsWithSpace` (vs `diffWords`) preserva espaços no output,
 * então a concatenação dos `value`s reconstrói `after` exatamente
 * (incluindo quebras de linha, que a UI renderiza via `whitespace-pre-wrap`). */
export function diffNotes(
  before: string | null | undefined,
  after: string | null | undefined,
): NotesDiffPart[] {
  const a = after ?? '';
  if (a.length === 0) return [];

  const b = before ?? '';
  if (b.length === 0) {
    // Sem baseline → tudo novo.
    return [{ kind: 'added', value: a }];
  }

  const changes = diffWordsWithSpace(b, a);
  const out: NotesDiffPart[] = [];
  for (const change of changes) {
    if (change.removed) {
      // Ignora: o texto `after` não contém isso (já foi tirado pela Karla).
      continue;
    }
    out.push({
      kind: change.added ? 'added' : 'unchanged',
      value: change.value,
    });
  }

  // Normalização: mesclar segmentos consecutivos do mesmo tipo. O
  // jsdiff pode retornar `[unchanged "a"], [added "b"], [unchanged " c"]`
  // onde "b c" faz mais sentido como um bloco só. Mesclar reduz
  // quantidade de `<mark>` renderizados e melhora a continuidade visual.
  return mergeAdjacent(out);
}

function mergeAdjacent(parts: NotesDiffPart[]): NotesDiffPart[] {
  const merged: NotesDiffPart[] = [];
  for (const part of parts) {
    const last = merged[merged.length - 1];
    if (last && last.kind === part.kind) {
      last.value += part.value;
    } else {
      merged.push({ ...part });
    }
  }
  return merged;
}
